import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO_MAIN, NAV_ITEMS } from "@/lib/constants";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-brand-forest/10 shadow-sm"
          : "bg-white/80 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
        <div className="flex items-center justify-between h-20 md:h-24">
          <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 group">
            <img
              src={LOGO_MAIN}
              alt="Hawaii Housing Foundation"
              className="h-12 md:h-14 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.02]"
              data-testid="header-logo-img"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-10" data-testid="desktop-nav">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `link-underline text-[13px] uppercase tracking-[0.2em] font-medium transition-colors duration-200 ${
                    isActive ? "text-brand-forest nav-link-active" : "text-brand-ink/70 hover:text-brand-forest"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <Link
              to="/contact"
              data-testid="header-get-help-btn"
              className="inline-flex items-center justify-center px-6 py-3 bg-brand-forest text-white text-[12px] uppercase tracking-[0.22em] font-semibold rounded-sm hover:bg-brand-forestDark transition-colors duration-300"
            >
              Get Help
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            className="lg:hidden p-2 text-brand-forest"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-nav"
          className="lg:hidden fixed inset-x-0 top-20 bottom-0 bg-white border-t border-brand-forest/10 overflow-y-auto animate-fade-in"
        >
          <div className="px-6 py-8 flex flex-col gap-6">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-2xl font-heading ${isActive ? "text-brand-forest" : "text-brand-ink"} hover:text-brand-gold transition-colors`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-get-help-btn"
              className="mt-4 inline-flex items-center justify-center px-6 py-4 bg-brand-forest text-white text-[12px] uppercase tracking-[0.22em] font-semibold rounded-sm hover:bg-brand-forestDark"
            >
              Get Help
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
